import { EQUIPMENT_SLOTS, type Equipment } from "@nano/contracts";
import { COSMETIC_BY_ID, STARTER_ITEM_IDS } from "../catalog/cosmetics.ts";

/**
 * Achievements shared by the client (progress, unlock moments) and the
 * Worker (claims). Only metrics the Worker can recompute from D1 pay gold;
 * the rest are badges earned on this device (client clock, local stats).
 */
export const ACHIEVEMENT_METRICS = [
  "levelsCompleted",
  "totalStars",
  "perfectLevels",
  "chaptersCompleted",
  "perfectChapters",
  "itemsPurchased",
  "slotsStyled",
  "epicOwned",
  "recoverySaved",
  "fastClears",
  "playDays",
] as const;
export type AchievementMetric = (typeof ACHIEVEMENT_METRICS)[number];

export const VERIFIED_METRICS: ReadonlySet<AchievementMetric> = new Set<AchievementMetric>([
  "levelsCompleted",
  "totalStars",
  "perfectLevels",
  "chaptersCompleted",
  "perfectChapters",
  "itemsPurchased",
  "slotsStyled",
  "epicOwned",
  "recoverySaved",
]);

export type AchievementStats = Readonly<Record<AchievementMetric, number>>;

/** A level clear under this time counts towards the "quick hands" badge. */
export const FAST_CLEAR_MS = 30_000;

interface Family {
  readonly id: string;
  readonly name: string;
  readonly badge: string;
  readonly metric: AchievementMetric;
  readonly tiers: readonly (readonly [target: number, gold: number])[];
  readonly describe: (target: number) => string;
}

const FAMILIES: readonly Family[] = [
  { id: "first-steps", name: "Bước chân đầu tiên", badge: "badge.key", metric: "levelsCompleted", tiers: [[1, 20]], describe: () => "Hoàn thành màn chơi đầu tiên" },
  {
    id: "levels",
    name: "Người sắp xếp",
    badge: "badge.gifts",
    metric: "levelsCompleted",
    tiers: [[10, 20], [25, 30], [50, 50], [75, 80], [100, 120]],
    describe: (n) => `Hoàn thành ${n} màn chơi`,
  },
  {
    id: "stars",
    name: "Hái sao",
    badge: "badge.constellation",
    metric: "totalStars",
    tiers: [[25, 20], [75, 30], [150, 50], [225, 80], [300, 120]],
    describe: (n) => `Thu thập ${n} sao`,
  },
  {
    id: "perfect",
    name: "Hoàn hảo",
    badge: "badge.gem-crown",
    metric: "perfectLevels",
    tiers: [[5, 20], [20, 40], [50, 70], [100, 120]],
    describe: (n) => `Đạt 3 sao ở ${n} màn`,
  },
  {
    id: "chapters",
    name: "Trang sách mới",
    badge: "badge.storybook",
    metric: "chaptersCompleted",
    tiers: [[1, 20], [3, 30], [5, 40], [8, 50], [10, 80]],
    describe: (n) => `Hoàn tất ${n} chương`,
  },
  {
    id: "perfect-chapters",
    name: "Vòng nguyệt quế",
    badge: "badge.laurel",
    metric: "perfectChapters",
    tiers: [[1, 30], [3, 60], [6, 90], [10, 140]],
    describe: (n) => `Đạt trọn 30 sao ở ${n} chương`,
  },
  {
    id: "shopper",
    name: "Tín đồ mua sắm",
    badge: "badge.shopping-bag",
    metric: "itemsPurchased",
    tiers: [[1, 15], [3, 25], [8, 40], [15, 60], [25, 90]],
    describe: (n) => `Sở hữu ${n} món đồ mua ở cửa hàng`,
  },
  {
    id: "stylist",
    name: "Gu thời trang",
    badge: "badge.mirror",
    metric: "slotsStyled",
    tiers: [[3, 20], [5, 30], [7, 50]],
    describe: (n) => `Diện cùng lúc đồ ở ${n} vị trí`,
  },
  {
    id: "treasures",
    name: "Báu vật quý hiếm",
    badge: "badge.tiara",
    metric: "epicOwned",
    tiers: [[1, 40], [3, 80]],
    describe: (n) => `Sở hữu ${n} món Sử thi`,
  },
  { id: "safe-journey", name: "Hành trình an toàn", badge: "badge.heart-lock", metric: "recoverySaved", tiers: [[1, 30]], describe: () => "Tạo mã khôi phục hành trình" },
  {
    id: "quick-hands",
    name: "Nhanh tay",
    badge: "badge.hourglass",
    metric: "fastClears",
    tiers: [[1, 0], [10, 0], [30, 0]],
    describe: (n) => `Hoàn thành ${n} màn dưới ${FAST_CLEAR_MS / 1000} giây`,
  },
  {
    id: "daily",
    name: "Ngày nào cũng vui",
    badge: "badge.sun-moon",
    metric: "playDays",
    tiers: [[3, 0], [7, 0], [14, 0], [30, 0]],
    describe: (n) => `Chơi vào ${n} ngày khác nhau`,
  },
];

const ROMAN = ["I", "II", "III", "IV", "V", "VI"];

export interface AchievementDef {
  readonly id: string;
  readonly family: string;
  /** 1-based tier inside the family. */
  readonly tier: number;
  readonly tierCount: number;
  readonly name: string;
  readonly description: string;
  readonly badgeAssetId: string;
  readonly metric: AchievementMetric;
  readonly target: number;
  /** Gold granted by the Worker on claim; 0 for device-only badges. */
  readonly rewardGold: number;
  readonly verified: boolean;
}

export const ACHIEVEMENTS: readonly AchievementDef[] = FAMILIES.flatMap((family) =>
  family.tiers.map(([target, gold], index): AchievementDef => {
    const verified = VERIFIED_METRICS.has(family.metric);
    return {
      id: family.tiers.length > 1 ? `${family.id}-${index + 1}` : family.id,
      family: family.id,
      tier: index + 1,
      tierCount: family.tiers.length,
      name: family.tiers.length > 1 ? `${family.name} ${ROMAN[index]}` : family.name,
      description: family.describe(target),
      badgeAssetId: family.badge,
      metric: family.metric,
      target,
      rewardGold: verified ? gold : 0,
      verified,
    };
  }),
);

export const ACHIEVEMENT_BY_ID: ReadonlyMap<string, AchievementDef> = new Map(ACHIEVEMENTS.map((def) => [def.id, def]));

export const ACHIEVEMENT_BADGE_IDS: readonly string[] = [...new Set(FAMILIES.map((family) => family.badge))];

export function emptyStats(): Record<AchievementMetric, number> {
  return Object.fromEntries(ACHIEVEMENT_METRICS.map((metric) => [metric, 0])) as Record<AchievementMetric, number>;
}

/** Level-derived metrics; chapters are blocks of ten levels per house. */
export function levelStats(levels: Iterable<{ houseId: string; levelId: number; bestStars: number }>) {
  let levelsCompleted = 0;
  let totalStars = 0;
  let perfectLevels = 0;
  const chapters = new Map<string, { cleared: number; stars: number }>();
  for (const level of levels) {
    levelsCompleted += 1;
    totalStars += level.bestStars;
    if (level.bestStars === 3) perfectLevels += 1;
    const key = `${level.houseId}:${Math.ceil(level.levelId / 10)}`;
    const chapter = chapters.get(key) ?? { cleared: 0, stars: 0 };
    chapters.set(key, { cleared: chapter.cleared + 1, stars: chapter.stars + level.bestStars });
  }
  let chaptersCompleted = 0;
  let perfectChapters = 0;
  for (const chapter of chapters.values()) {
    if (chapter.cleared >= 10) chaptersCompleted += 1;
    if (chapter.stars >= 30) perfectChapters += 1;
  }
  return { levelsCompleted, totalStars, perfectLevels, chaptersCompleted, perfectChapters };
}

/** Wardrobe-derived metrics; starter hats are free and do not count as purchases. */
export function wardrobeStats(owned: readonly string[], equipped: Equipment) {
  const bought = owned.filter((id) => !STARTER_ITEM_IDS.includes(id));
  return {
    itemsPurchased: bought.length,
    epicOwned: bought.filter((id) => COSMETIC_BY_ID.get(id)?.rarity === "epic").length,
    slotsStyled: EQUIPMENT_SLOTS.filter((slot) => Boolean(equipped[slot])).length,
  };
}

export function achievementProgress(def: AchievementDef, stats: AchievementStats): { current: number; reached: boolean } {
  const current = Math.min(stats[def.metric], def.target);
  return { current, reached: stats[def.metric] >= def.target };
}
